/** ONE Smart Attach — internal console. Commercial → Smart Attach.
 *  Tabs: Overview/Reports · Opportunities · Compatibility · Config. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/app/StatusBadge";
import { ShoppingBag, LayoutDashboard, Link2, Layers, Settings2, Plus, Trash2, BarChart3 } from "lucide-react";

const TABS = [["overview", "Reports", LayoutDashboard], ["analytics", "Analytics", BarChart3], ["opps", "Opportunities", Link2],
  ["compat", "Compatibility", Layers], ["cfg", "Config", Settings2]];
const money = (a, c = "INR") => a == null ? "—" : new Intl.NumberFormat("en-IN", { style: "currency", currency: c || "INR", maximumFractionDigits: 0 }).format(a);

export default function SmartAttach() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="max-w-[1200px] mx-auto px-4 py-5" data-testid="smart-attach-admin">
      <div className="flex items-center gap-3 mb-1"><ShoppingBag className="h-6 w-6 text-emerald-600" />
        <h1 className="text-2xl font-bold text-slate-900">Smart Attach</h1></div>
      <p className="text-sm text-slate-500 mb-5">Post-purchase accessory, peripheral & services cross-sell across all brands.</p>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-5">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sa-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px ${tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "analytics" && <Analytics />}
      {tab === "opps" && <Opps />}
      {tab === "compat" && <Compat />}
      {tab === "cfg" && <Config />}
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/smart-attach/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Sk />;
  const tiles = [["Emails sent", d.emails_sent], ["Interested", d.interested], ["Converted", d.converted],
    ["Attach rate", `${d.attach_rate_pct}%`], ["Accessory revenue", money(d.accessory_revenue)]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sa-overview">
      {tiles.map(([l, v]) => (
        <div key={l} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <div className="text-xl font-bold text-slate-900">{v ?? 0}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div>
        </div>
      ))}
    </div>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/smart-attach/analytics").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Sk />;
  const tiles = [["Opportunities", d.total], ["Interested", d.interested], ["Converted", d.converted],
    ["Attach rate", `${d.attach_rate_pct}%`], ["Accessory revenue", money(d.accessory_revenue)]];
  const Bars = ({ title, rows, testId }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-[12px] text-slate-400 py-6 text-center">No data yet — recommendations appear here once orders generate Smart Attach.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => {
            const max = Math.max(...rows.map((x) => x.opportunities), 1);
            return (
              <div key={r.key} data-testid={`sa-analytics-row-${(r.key || "").toString().toLowerCase()}`}>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="font-medium text-slate-800 truncate pr-2">{r.key}</span>
                  <span className="text-slate-500 shrink-0">{r.opportunities} opp · {r.attach_rate_pct}% · {money(r.revenue)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-violet-500" style={{ width: `${(r.opportunities / max) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="sa-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <div className="text-xl font-bold text-slate-900">{v ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Bars title="By brand" rows={d.by_brand} testId="sa-analytics-brand" />
        <Bars title="By product variant" rows={d.by_variant} testId="sa-analytics-variant" />
      </div>
    </div>
  );
}

function Opps() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/smart-attach/opportunities").then(({ data }) => setRows(data.items)).catch(() => setRows([])); }, []);
  if (!rows) return <Sk />;
  return (
    <Panel title={`Opportunities (${rows.length})`}>
      {rows.length === 0 ? <Empty text="No Smart Attach opportunities yet — deliver a product order to generate one" /> : (
        <Table head={["Code", "Customer", "Product", "Order", "Recs", "Interests", "Status"]} testId="sa-opps-table">
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`sa-opp-${r.id}`}>
              <Td mono>{r.code}</Td><Td>{r.customer_name}</Td><Td>{r.product_name}<div className="text-[11px] text-slate-400">{r.brand}</div></Td>
              <Td mono>{r.order_code}</Td><Td>{r.recommendation_count}</Td><Td>{(r.interests || []).length}</Td>
              <Td><StatusBadge value={r.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function Compat() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ brand: "APPLE", source_model_rule: "", accessory_name: "", accessory_sku: "", group: "ESSENTIAL", priority: "RECOMMENDED", price: "", product_url: "" });
  const load = useCallback(() => api.get("/smart-attach/compat", { params: q ? { q } : {} }).then(({ data }) => setRows(data.items)).catch(() => setRows([])), [q]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/smart-attach/compat", { ...f, price: f.price ? Number(f.price) : null }); toast.success("Compatibility added"); setAdding(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (id) => { try { await api.delete(`/smart-attach/compat/${id}`); load(); } catch { toast.error("Failed"); } };
  if (!rows) return <Sk />;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input placeholder="Search accessory or model…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" data-testid="sa-compat-search" />
        <Button onClick={() => setAdding((v) => !v)} data-testid="sa-add-compat" className="ml-auto bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" /> Add compatibility</Button>
      </div>
      {adding && (
        <Panel title="New compatibility relationship">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sa-compat-form">
            <L label="Brand"><Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value.toUpperCase() })} data-testid="sa-cf-brand" /></L>
            <L label="Source model rule (e.g. Surface Pro / ANY)"><Input value={f.source_model_rule} onChange={(e) => setF({ ...f, source_model_rule: e.target.value })} data-testid="sa-cf-model" /></L>
            <L label="Accessory name"><Input value={f.accessory_name} onChange={(e) => setF({ ...f, accessory_name: e.target.value })} data-testid="sa-cf-name" /></L>
            <L label="Accessory SKU"><Input value={f.accessory_sku} onChange={(e) => setF({ ...f, accessory_sku: e.target.value })} data-testid="sa-cf-sku" /></L>
            <L label="Group"><select value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="sa-cf-group" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{["ESSENTIAL", "PRODUCTIVITY", "CONNECTIVITY", "PROTECTION", "MOBILITY", "POWER", "AUDIO", "WORKSPACE", "SERVICES"].map((g) => <option key={g}>{g}</option>)}</select></L>
            <L label="Priority"><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="sa-cf-priority" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{["MUST_CONSIDER", "HIGHLY_RECOMMENDED", "RECOMMENDED", "OPTIONAL"].map((p) => <option key={p}>{p}</option>)}</select></L>
            <L label="Price (INR)"><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="sa-cf-price" /></L>
            <L label="SUNTRONIX product URL"><Input value={f.product_url} onChange={(e) => setF({ ...f, product_url: e.target.value })} placeholder="/brand/product" data-testid="sa-cf-url" /></L>
          </div>
          <div className="flex justify-end mt-3"><Button onClick={create} data-testid="sa-cf-save" className="bg-emerald-700 hover:bg-emerald-800">Save</Button></div>
        </Panel>
      )}
      <Panel title={`Compatibility Master (${rows.length})`}>
        {rows.length === 0 ? <Empty text="No relationships" /> : (
          <Table head={["Brand", "Model rule", "Accessory", "Group", "Priority", "Price", ""]} testId="sa-compat-table">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`sa-compat-${r.id}`}>
                <Td>{r.brand}</Td><Td>{r.source_model_rule}</Td><Td>{r.accessory_name}<div className="text-[11px] text-slate-400">{r.accessory_sku}</div></Td>
                <Td>{r.group}</Td><Td>{r.priority?.replace(/_/g, " ")}</Td><Td>{money(r.price, r.currency)}</Td>
                <Td><button onClick={() => del(r.id)} data-testid={`sa-del-${r.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}

function Config() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/smart-attach/config").then(({ data }) => setC(data)).catch(() => {}); }, []);
  const save = async () => { try { await api.patch("/smart-attach/config", c); toast.success("Saved"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const resetPriming = async () => {
    try {
      const { data } = await api.patch("/smart-attach/config", { auto_primed_brands: [] });
      setC(data); toast.success("First-send review reset — the next email per brand will be held for review");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!c) return <Sk />;
  return (
    <Panel title="Smart Attach configuration">
      <div className="space-y-4 max-w-2xl" data-testid="sa-config">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.enabled} onChange={(e) => setC({ ...c, enabled: e.target.checked })} data-testid="sa-cfg-enabled" /> Engine enabled</label>
        <div className="grid grid-cols-2 gap-3">
          <L label="Trigger event"><select value={c.trigger_event} onChange={(e) => setC({ ...c, trigger_event: e.target.value })} data-testid="sa-cfg-trigger" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option value="DELIVERED">Delivery confirmed</option><option value="INVOICE_ISSUED">Invoice issued</option></select></L>
          <L label="Website base URL"><Input value={c.website_base || ""} onChange={(e) => setC({ ...c, website_base: e.target.value })} data-testid="sa-cfg-website" /></L>
          <L label="Max primary recommendations"><Input type="number" value={c.max_primary} onChange={(e) => setC({ ...c, max_primary: Number(e.target.value) })} data-testid="sa-cfg-primary" /></L>
          <L label="Max extra recommendations"><Input type="number" value={c.max_extra} onChange={(e) => setC({ ...c, max_extra: Number(e.target.value) })} data-testid="sa-cfg-extra" /></L>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.email_mode === "auto"} onChange={(e) => setC({ ...c, email_mode: e.target.checked ? "auto" : "review" })} data-testid="sa-cfg-auto" /> Auto-send emails for ALL brands (otherwise prepared for review)</label>
        <div data-testid="sa-cfg-auto-brands">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Auto-send on delivery for specific brands</div>
          <div className="flex flex-wrap gap-3">
            {(c.enabled_brands || []).map((b) => {
              const on = (c.auto_send_brands || []).includes(b);
              return (
                <label key={b} className="flex items-center gap-1.5 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer hover:bg-slate-50" data-testid={`sa-cfg-autobrand-${b}`}>
                  <input type="checkbox" checked={on} disabled={c.email_mode === "auto"}
                    onChange={(e) => {
                      const set = new Set(c.auto_send_brands || []);
                      e.target.checked ? set.add(b) : set.delete(b);
                      setC({ ...c, auto_send_brands: Array.from(set) });
                    }} />
                  <span className="capitalize">{b.toLowerCase()}</span>
                </label>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">When ON, Smart Attach emails for that brand send automatically on delivery instead of waiting in the review outbox.</div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="sa-cfg-safety">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Auto-send safety</div>
          <label className="flex items-center gap-2 text-sm">
            Daily auto-send cap per brand
            <input type="number" min="0" value={c.auto_send_daily_cap ?? 50}
              onChange={(e) => setC({ ...c, auto_send_daily_cap: Number(e.target.value) })}
              data-testid="sa-cfg-daily-cap"
              className="w-24 border border-slate-200 rounded-md px-2 py-1 text-sm" />
            <span className="text-[11px] text-slate-400">(0 = no cap)</span>
          </label>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-slate-500">
              The <b>first</b> email for each brand is always held for review before auto-send starts.
              {(c.auto_primed_brands || []).length > 0 && (
                <span> Primed: {(c.auto_primed_brands || []).map((b) => b.toLowerCase()).join(", ")}.</span>
              )}
            </div>
            <button type="button" onClick={resetPriming} data-testid="sa-cfg-reset-priming"
              className="text-[12px] border border-amber-300 text-amber-700 rounded-md px-2.5 py-1 hover:bg-amber-100 shrink-0">
              Reset first-send review
            </button>
          </div>
        </div>
        <Button onClick={save} data-testid="sa-cfg-save" className="bg-emerald-700 hover:bg-emerald-800">Save configuration</Button>
      </div>
    </Panel>
  );
}

const Panel = ({ title, children }) => (<section className="border border-slate-200 rounded-lg bg-white p-4"><h2 className="text-[13px] font-semibold text-slate-800 mb-3">{title}</h2>{children}</section>);
const Table = ({ head, children, testId }) => (<div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}><thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">{head.map((h, i) => <th key={i} className="pb-2 pr-3 font-medium">{h}</th>)}</tr></thead><tbody>{children}</tbody></table></div>);
const Td = ({ children, mono }) => <td className={`py-2 pr-3 align-top ${mono ? "font-mono text-[12px]" : ""}`}>{children}</td>;
const L = ({ label, children }) => <div><label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</label>{children}</div>;
const Empty = ({ text }) => <div className="text-[13px] text-slate-400 py-6 text-center">{text}</div>;
const Sk = () => <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded animate-pulse" />)}</div>;
